// Shared helper for building a friendly WhatsApp handover message
// that mentions the park reason. Used across the SalesmanDashboard
// follow-up cards, the Lead Sessions swipe/tap action, and the
// Admin Parked Board card action buttons.
//
// The message is pre-filled but never sent automatically — the
// salesman still lands on WhatsApp and can edit before hitting send.

const TEMPLATES = {
  design: (name, product) =>
    `Hi ${name}, following up on your ${product} enquiry — our team is preparing fresh design options for you. Will share the previews shortly. Thanks for your patience!`,
  new_stock: (name, product) =>
    `Hi ${name}, quick update on your ${product} — we're waiting for the right stock to arrive. Will be in touch as soon as it's here.`,
  order_process: (name, product) =>
    `Hi ${name}, your ${product} is with our production team. Will share progress updates in the coming days.`,
  ready_for_delivery: (name, product) =>
    `Hi ${name}, great news — your ${product} is ready! Would you like to schedule a convenient time to visit us?`,
  customer_approval: (name, product) =>
    `Hi ${name}, gentle nudge — awaiting your confirmation on the ${product} we discussed. Please share your thoughts whenever convenient.`,
};

const FALLBACK = (name, product) =>
  `Hi ${name}, hope you're doing well! Just following up on your ${product} enquiry — do let me know if you have any questions.`;

/**
 * Build the WhatsApp message text.
 * @param {object} opts
 * @param {string} opts.client_name  - Client's first name / display name
 * @param {string} [opts.product_name] - Product referenced in the lead
 * @param {string} [opts.park_action] - One of design | new_stock | order_process | ready_for_delivery | customer_approval
 * @returns {string} Friendly, ready-to-send text
 */
export const buildHandoverText = ({ client_name, product_name, park_action } = {}) => {
  const name = (client_name || 'there').trim();
  const product = (product_name || 'jewellery').trim();
  const template = TEMPLATES[park_action];
  return template ? template(name, product) : FALLBACK(name, product);
};

// wa.me deep link — strips non-digits from phone, auto-prefixes 91 for 10-digit
// Indian numbers, and appends the pre-filled handover text when supplied.
export const waLinkWithText = (phone, text) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '#';
  const withCountry = digits.length === 10 ? `91${digits}` : digits;
  const base = `https://wa.me/${withCountry}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};
